import PropTypes from 'prop-types';
import Form, {
  FormGroup,
  FormControl,
  FormSubmit,
  FormActions,
  TextFieldControl,
  EmailFieldControl,
  CountryFieldControl,
  submitForm,
} from '@/components/Forms';
import { notifyError } from '@/components/Toaster';
import downloadProgramFormValidationSchema from './downloadProgramFormValidationSchema';

const defaultProps = {
  onSuccess: () => null,
  onError: () => notifyError(`
    Ha ocurrido un error cuando intentamos registrar tu solicitud.
    Por favor inténtalo nuevamente.
  `),
};

const propTypes = {
  formUrl: PropTypes.string.isRequired,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
};

export default function DownloadProgramForm({
  formUrl,
  onSuccess,
  onError,
}) {
  return (
    <Form
      initialValues={{
        firstname: '',
        lastname: '',
        email: '',
        mobilephone: '',
        city: '',
        country: '',
        website: '',
      }}
      onSubmit={(values, {
        setSubmitting,
        resetForm,
      }) => {
        if (values.website === '') {
          submitForm(formUrl, values, {
            setSubmitting,
            onSuccess: function() {
              resetForm();
              onSuccess();
            },
            onError: function(errors) {
              onError();
            },
          }); 
        }
      }}
      validationSchema={downloadProgramFormValidationSchema}
      validateOnMount
    >
      {({
        handleChange,
        handleBlur,
        isSubmitting,
        errors,
        touched,
        values,
        isValid,
        setFieldValue,
        validateForm,
      }) => {
        const countryHasError = touched.country && errors.country !== undefined;
        
        return (
          <div className="flex flex-col w-full min-h-full space-y-6">
            <div className="flex-1 space-y-6">
              <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-6">
                <FormGroup>
                  <FormControl
                    htmlFor="firstname"
                    label="Nombres"
                    error={errors.firstname}
                    touched={touched.firstname}
                  >
                    <TextFieldControl
                      id="firstname"
                      name="firstname"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.firstname}
                      disabled={isSubmitting}
                      error={errors.firstname}
                      touched={touched.firstname}
                      light={false}
                      required
                    />
                  </FormControl>
                </FormGroup>
                <FormGroup>
                  <FormControl
                    htmlFor="lastname"
                    label="Apellidos"
                    error={errors.lastname}
                    touched={touched.lastname}
                  >
                    <TextFieldControl
                      id="lastname"
                      name="lastname"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.lastname}
                      disabled={isSubmitting}
                      error={errors.lastname}
                      touched={touched.lastname}
                      light={false}
                      required
                    />
                  </FormControl>
                </FormGroup>
                <FormGroup>
                  <FormControl
                    htmlFor="email"
                    label="Correo electrónico"
                    error={errors.email}
                    touched={touched.email}
                  >
                    <EmailFieldControl
                      id="email"
                      name="email"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.email}
                      disabled={isSubmitting}
                      error={errors.email}
                      touched={touched.email}
                      light={false}
                      required
                    />
                  </FormControl>
                </FormGroup>
                <FormGroup>
                  <FormControl
                    htmlFor="mobilephone"
                    label="Número de teléfono móvil"
                    error={errors.mobilephone}
                    touched={touched.mobilephone}
                  >
                    <TextFieldControl
                      id="mobilephone"
                      name="mobilephone"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.mobilephone}
                      disabled={isSubmitting}
                      error={errors.mobilephone}
                      touched={touched.mobilephone}
                      light={false}
                      required
                    />
                  </FormControl>
                </FormGroup>
                <FormGroup>
                  <FormControl
                    htmlFor="city"
                    label="Ciudad"
                    error={errors.city}
                    touched={touched.city}
                  >
                    <TextFieldControl
                      id="city"
                      name="city"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.city}
                      disabled={isSubmitting}
                      error={errors.city}
                      touched={touched.city}
                      light={false}
                      required
                    />
                  </FormControl>
                </FormGroup>
                <FormGroup>
                  <FormControl
                    htmlFor="country"
                    label="País/región"
                    error={errors.country}
                    touched={touched.country}
                  >
                    <CountryFieldControl
                      id="country"
                      name="country"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      setFieldValue={setFieldValue}
                      validateForm={validateForm}
                      value={values.country}
                      disabled={isSubmitting}
                      error={errors.country}
                      hasError={countryHasError}
                      light={false}
                      required
                    />
                  </FormControl>
                </FormGroup>
              </div>
              <input
                className="h"
                autocomplete="off"
                type="text"
                id="website"
                name="website"
                placeholder="Sitio web"
                onChange={handleChange}
                value={values.website}
              />
              <FormActions>
                <FormSubmit
                  label="Descargar programa"
                  submitting={isSubmitting}
                  disabled={isValid === false || values.website !== ''}
                />
              </FormActions>
            </div>
            <div className="flex flex-col space-y-4">
              <div className="text-xs leading-5 text-gray-700">
                Estamos comprometidos con su privacidad. AIBES utiliza la información
                que nos proporciona para contactarlo sobre nuestro contenido, productos
                y servicios relevantes. Puede darse de baja de estas comunicaciones en
                cualquier momento. Para obtener más información, consulte nuestra&nbsp;
                <a className="font-medium underline" href="https://www.aibes.org/legal/privacidad/" target="_blank" rel="noopener noreferrer">
                  Política de privacidad
                </a>
                .
              </div>
            </div>
          </div>
        );
      }}
    </Form>
  );
}

DownloadProgramForm.defaultProps = defaultProps;
DownloadProgramForm.propTypes = propTypes;
